/**
 * DAF-19 — structural verification for composite report sections.
 *
 * Consumes `SECTION_SCHEMAS` from `src/config/sectionSchemas.ts` and
 * emits H1-compatible findings when a rollup section violates its
 * contract (missing subsections, thin word count, missing table/callout).
 */
import type { SiteRagReport } from '../../types/siteRag';
import {
  getCompositeSectionSchema,
  listCompositeSectionSchemas,
  type CompositeSectionSchema,
} from '../../config/sectionSchemas';
import type { SectionDriftFinding } from './sectionDriftVerifier';

const REF_TOKEN_RE = /\[Ref\s+\d+\]/gi;
const TABLE_RE = /\|.+\|/m;
const CALLOUT_RE = /^>\s/m;

function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function citationDensity(text: string): number {
  const words = wordCount(text);
  if (words === 0) return 0;
  const refs = text.match(REF_TOKEN_RE)?.length ?? 0;
  return refs / words;
}

function extractSectionBody(report: SiteRagReport, sectionId: string): string {
  const r = report as unknown as Record<string, unknown>;
  const direct = r[sectionId];
  if (typeof direct === 'string') return direct;
  if (sectionId === 'execSummary') {
    const exec = report.executiveSummary ?? report.summary ?? '';
    return typeof exec === 'string' ? exec : '';
  }
  if (direct != null && typeof direct === 'object') {
    const parts: string[] = [];
    for (const v of Object.values(direct as Record<string, unknown>)) {
      if (typeof v === 'string') parts.push(v);
      else if (Array.isArray(v)) parts.push(v.filter((x) => typeof x === 'string').join('\n'));
    }
    return parts.join('\n\n');
  }
  return '';
}

function hasSubsection(text: string, slug: string): boolean {
  const heading = slug.replace(/-/g, ' ');
  const h2 = new RegExp(`^##\\s+.*${heading.replace(/\s+/g, '\\s+')}`, 'im');
  const h3 = new RegExp(`^###\\s+.*${heading.replace(/\s+/g, '\\s+')}`, 'im');
  const inline = new RegExp(`\\b${heading.replace(/\s+/g, '\\s+')}\\b`, 'i');
  return h2.test(text) || h3.test(text) || inline.test(text);
}

function buildKeywordFinding(
  report: SiteRagReport,
  sectionId: string,
  severity: 'block' | 'warn',
  message: string,
  missingKeywords: string[],
): SectionDriftFinding {
  return {
    detector: 'keyword',
    reportTypeId: report.reportType ?? 'feasibility',
    sectionId,
    severity,
    overlap: 1,
    requiredKeywordCount: missingKeywords.length,
    matchedKeywords: [],
    missingKeywords,
    prohibitedHits: [],
    message,
  };
}

function verifySchema(
  report: SiteRagReport,
  schema: CompositeSectionSchema,
): SectionDriftFinding[] {
  const body = extractSectionBody(report, schema.sectionId);
  if (!body.trim()) return [];

  const findings: SectionDriftFinding[] = [];
  const rules = schema.structuralRules;

  if (rules.minWordCount != null && wordCount(body) < rules.minWordCount) {
    findings.push(
      buildKeywordFinding(
        report,
        schema.sectionId,
        'warn',
        `${schema.sectionId}: word count ${wordCount(body)} below minimum ${rules.minWordCount}.`,
        [`minWordCount:${rules.minWordCount}`],
      ),
    );
  }

  if (rules.maxWordCount != null && wordCount(body) > rules.maxWordCount) {
    findings.push(
      buildKeywordFinding(
        report,
        schema.sectionId,
        'warn',
        `${schema.sectionId}: word count ${wordCount(body)} exceeds maximum ${rules.maxWordCount}.`,
        [`maxWordCount:${rules.maxWordCount}`],
      ),
    );
  }

  for (const sub of schema.requiredSubsections) {
    if (!hasSubsection(body, sub)) {
      findings.push(
        buildKeywordFinding(
          report,
          schema.sectionId,
          'warn',
          `${schema.sectionId}: missing required subsection "${sub}".`,
          [sub],
        ),
      );
    }
  }

  if (rules.requiresTable && !TABLE_RE.test(body)) {
    findings.push(
      buildKeywordFinding(
        report,
        schema.sectionId,
        'warn',
        `${schema.sectionId}: expected markdown table not found.`,
        ['table'],
      ),
    );
  }

  if (rules.requiresCallout && !CALLOUT_RE.test(body)) {
    findings.push(
      buildKeywordFinding(
        report,
        schema.sectionId,
        'warn',
        `${schema.sectionId}: expected callout block (>) not found.`,
        ['callout'],
      ),
    );
  }

  if (rules.citationDensityMin != null) {
    const density = citationDensity(body);
    if (density < rules.citationDensityMin) {
      findings.push(
        buildKeywordFinding(
          report,
          schema.sectionId,
          'warn',
          `${schema.sectionId}: citation density ${density.toFixed(2)} below minimum ${rules.citationDensityMin}.`,
          ['citation-density'],
        ),
      );
    }
  }

  const missingHeadings = schema.canonicalHeadings.filter(
    (h) => !new RegExp(`\\b${h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(body),
  );
  if (missingHeadings.length > 0) {
    findings.push(
      buildKeywordFinding(
        report,
        schema.sectionId,
        'warn',
        `${schema.sectionId}: canonical headings absent: ${missingHeadings.join(', ')}.`,
        missingHeadings,
      ),
    );
  }

  return findings;
}

/** Run structural checks for all composite schemas present in the report. */
export function verifyCompositeSectionStructure(report: SiteRagReport): SectionDriftFinding[] {
  const out: SectionDriftFinding[] = [];
  for (const schema of listCompositeSectionSchemas()) {
    const body = extractSectionBody(report, schema.sectionId);
    if (!body.trim()) continue;
    out.push(...verifySchema(report, schema));
  }
  return out;
}

export function verifyCompositeSectionById(
  report: SiteRagReport,
  sectionId: string,
): SectionDriftFinding[] {
  const schema = getCompositeSectionSchema(sectionId);
  if (!schema) return [];
  const body = extractSectionBody(report, sectionId);
  if (!body.trim()) return [];
  return verifySchema(report, schema);
}
